import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PRODUCTS, accentBg, formatPrice } from '../data/products';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    key: 'concern',
    title: 'What is your skin asking for?',
    options: [
      { value: 'hydration', label: 'Deep hydration' },
      { value: 'brightening', label: 'A glowing complexion' },
      { value: 'firming', label: 'Firmness & bounce' },
      { value: 'fine_lines', label: 'Smoothing fine lines' },
    ],
  },
  {
    key: 'skin_type',
    title: 'How would you describe your skin?',
    options: [
      { value: 'dry', label: 'Dry & thirsty' },
      { value: 'oily', label: 'Oily / combination' },
      { value: 'sensitive', label: 'Sensitive' },
      { value: 'normal', label: 'Normal & balanced' },
    ],
  },
  {
    key: 'age',
    title: 'Your age range?',
    options: [
      { value: '14-24', label: '14 – 24' },
      { value: '25-34', label: '25 – 34' },
      { value: '35-49', label: '35 – 49' },
      { value: '50+', label: '50 +' },
    ],
  },
  {
    key: 'frequency',
    title: 'How often do you mask?',
    options: [
      { value: 'rarely', label: 'Almost never' },
      { value: 'weekly', label: '1× a week' },
      { value: 'twice', label: '2-3× a week' },
      { value: 'daily', label: 'Daily ritual' },
    ],
  },
];

export default function QuizPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { addItem } = useCart();
  const navigate = useNavigate();

  const submit = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/quiz`, { answers: finalAnswers });
      setResult(data.recommended);
    } catch (e) {
      const slug = finalAnswers.concern === 'firming' || finalAnswers.concern === 'fine_lines'
        ? 'collagen-smoothing-mask'
        : finalAnswers.concern === 'brightening'
        ? 'vitamin-c-glow-mask'
        : 'hyaluronic-hydration-mask';
      setResult(PRODUCTS.find((p) => p.slug === slug));
    } finally {
      setSubmitting(false);
    }
  };

  const choose = (option) => {
    const q = QUESTIONS[step];
    const next = { ...answers, [q.key]: option.value };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) {
      setStep(step + 1);
    } else {
      submit(next);
    }
  };

  if (result) {
    return (
      <div data-testid="quiz-result" className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className={`md:col-span-6 aspect-[4/5] ${accentBg(result.accent, true)} overflow-hidden`}>
          <img src={result.image} alt={result.name} className="w-full h-full object-contain p-10" />
        </div>
        <div className="md:col-span-6">
          <p className="overline mb-3">Your perfect ritual</p>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05]">{result.name}</h1>
          <p className="mt-5 text-lum-text2">{result.short_description}</p>
          <ul className="mt-6 space-y-2 text-sm">
            {result.benefits.map((b) => <li key={b}>— {b}</li>)}
          </ul>
          <div className="mt-8 flex gap-4">
            <button
              data-testid="quiz-add-to-bag"
              onClick={() => { addItem({ product: result, packSize: 'pack' }); toast.success('Added to your bag.'); }}
              className="btn-primary"
            >
              Add pack · {formatPrice(result.pack_price)}
            </button>
            <button onClick={() => navigate(`/product/${result.slug}`)} className="btn-secondary">Learn more</button>
          </div>
          <button onClick={() => { setResult(null); setStep(0); setAnswers({}); }} className="btn-ghost mt-8 inline-flex">Retake the quiz</button>
        </div>
      </div>
    );
  }

  const q = QUESTIONS[step];
  return (
    <div data-testid="quiz-page" className="max-w-3xl mx-auto px-6 py-20 md:py-32">
      <div className="flex justify-between text-xs uppercase tracking-[0.22em] text-lum-text2 mb-8">
        <span>Step {step + 1} / {QUESTIONS.length}</span>
        <button onClick={() => step > 0 && setStep(step - 1)} className="hover:text-lum-text">{step > 0 ? '← Back' : ''}</button>
      </div>
      <div className="h-[2px] bg-lum-bg3 mb-12">
        <div className="h-full bg-lum-text transition-all" style={{ width: `${((step) / QUESTIONS.length) * 100}%` }} />
      </div>
      <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">{q.title}</h1>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
        {q.options.map((o) => (
          <button
            key={o.value}
            data-testid={`quiz-option-${o.value}`}
            disabled={submitting}
            onClick={() => choose(o)}
            className="p-6 border border-lum-border text-left hover:bg-lum-bg2 transition-colors group"
          >
            <p className="font-serif-display text-2xl group-hover:translate-x-1 transition-transform">{o.label}</p>
          </button>
        ))}
      </div>
      {submitting && <p className="mt-8 text-center text-lum-text2 text-sm">Finding your ritual…</p>}
    </div>
  );
}
